import classNames from "classnames";
import {
  DISPLAY_DATE,
  FILTER_DATE_INPUT_FORMAT,
  YYYY_MM_DD,
} from "constants/date";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { FieldInputProps, getIn, useFormikContext } from "formik";
import { forwardRef, ReactNode, useId } from "react";
import ReactDatePicker, { DatePickerProps } from "react-datepicker";
import { checkForMonthDisable, CustomHeader } from "./DateInputCustomUI";
import { HelperText } from "components/DesignSystem/HelperText/HelperText";
import { BareInput, InputProps } from "../TextInput/TextInput";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { createPortal } from "react-dom";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { useToast } from "hooks/useToast";

dayjs.extend(customParseFormat);

type DateInputProps = {
  label?: ReactNode;
  placeholder?: string;
  required?: boolean;
  onDateChange?: (v: Date) => void;
  overriddenOnChange?: (v: Date) => void;
  dateFormat?: string;
  saveFormat?: string;
  customDatePickerInputTrigger?: ReactNode;
  error?: string | undefined;
  showErrorOnceTouched?: boolean;
  getDisabledTooltipContent?: (
    date: Date,
    minDate?: Date,
    maxDate?: Date | null
  ) => ReactNode;
} & FieldInputProps<any> &
  InputProps &
  Omit<
    DatePickerProps,
    "selectsRange" | "selectsMultiple" | "showMonthYearDropdown"
  >;

const CustomInput = forwardRef(
  (
    {
      required,
      label,
      placeholder,
      hideError,
      error,
      showErrorOnceTouched,
      ...props
    }: DateInputProps,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const { errors, touched } = useFormikContext();
    let hasError = getIn(errors, props.name) && getIn(touched, props.name);

    if (!showErrorOnceTouched) {
      hasError = getIn(errors, props.name);
    }

    return (
      <div className="t-relative t-block t-w-full [&>div>div>img]:!t-ml-0 [&>div>div>svg]:!t-ml-0">
        <BareInput
          {...props}
          ref={ref}
          label={label}
          placeholder={placeholder}
          required={required}
          customType={hasError ? "error" : props.customType}
          id={props.portalId || props.name}
          rightComponent={
            <label
              htmlFor={props.portalId || props.name}
              className="t-cursor-pointer t-flex t-items-center"
            >
              <CalendarIcon />
            </label>
          }
        />
        {!hideError && hasError && (
          <HelperText variant="error">
            {/* @ts-ignore */}
            {getIn(errors, props.name)}
          </HelperText>
        )}
      </div>
    );
  }
);

// TODO: Add BareDateInput

export const DateInput = ({
  label,
  required = false,
  placeholder = DISPLAY_DATE,
  onDateChange,
  dateFormat = FILTER_DATE_INPUT_FORMAT,
  saveFormat = YYYY_MM_DD,
  showMonthDropdown = true,
  showYearDropdown = true,
  customDatePickerInputTrigger,
  getDisabledTooltipContent,
  overriddenOnChange,
  ...props
}: DateInputProps) => {
  const { alertToast } = useToast();
  const excludeMonths = props.excludeDates?.map((date) =>
    dayjs(date instanceof Date ? date : date.date).month()
  );
  const excludeYear = props.excludeDates?.map((date) =>
    dayjs(date instanceof Date ? date : date.date).year()
  );
  const { setFieldValue } = useFormikContext();

  const onChange = (
    date: Date | Date[] | [Date | null, Date | null] | null,
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    // Handle range date selection [Date | null, Date | null]
    if (Array.isArray(date) && date.length === 2) {
      const firstDate = date[0];
      if (overriddenOnChange && firstDate) {
        overriddenOnChange(firstDate);
        return;
      }

      if (props.name && firstDate) {
        const savingDate = dayjs(firstDate).format(saveFormat);
        setFieldValue(props.name, savingDate);
      }
      if (firstDate) {
        onDateChange?.(firstDate);
      }
    }
    // Handle multiple date selection (Date[])
    else if (Array.isArray(date)) {
      const firstDate = date[0];
      if (overriddenOnChange && firstDate) {
        overriddenOnChange(firstDate);
        return;
      }

      if (props.name && firstDate) {
        const savingDate = dayjs(firstDate).format(saveFormat);
        setFieldValue(props.name, savingDate);
      }
      if (firstDate) {
        onDateChange?.(firstDate);
      }
    }
    // Handle single date selection
    else if (date && !Array.isArray(date)) {
      if (overriddenOnChange) {
        overriddenOnChange(date);
        return;
      }

      if (props.name) {
        const savingDate = dayjs(date).format(saveFormat);
        setFieldValue(props.name, savingDate);
      }
      onDateChange?.(date);
    }
    // Handle null date
    else if (date === null) {
      if (props.name) {
        setFieldValue(props.name, null);
      }
    }
  };

  const onChangeRaw = (e: React.ClipboardEvent<HTMLInputElement>) => {
    //@ts-ignore
    const date = e.target.value;

    if (!date) {
      return;
    }

    const parsedDate = dayjs(date);
    const isBeforeMinDate =
      props.minDate && parsedDate.isBefore(props.minDate, "day");
    const isAfterMaxDate =
      props.maxDate && parsedDate.isAfter(props.maxDate, "day");

    if (parsedDate?.isValid() && !isBeforeMinDate && !isAfterMaxDate) {
      onChange(parsedDate.toDate());
    } else {
      onChange(null);
      alertToast({
        title: "Invalid date",
        message: "Please paste a valid date",
      });
    }
  };

  const defaultPortalId = useId();
  const finalPortalId = `date-portal-${props.portalId || defaultPortalId}`;

  // Safari and Firefox can't natively parse non-ISO strings like "Dec-2026",
  // so fall back to parsing with the format the value was saved in
  let selectedDate = props.value ? dayjs(props.value) : null;

  if (selectedDate && !selectedDate.isValid()) {
    selectedDate = dayjs(props.value, saveFormat);
  }

  return (
    <>
      {/* We can't style the portal created by date picker, that's why */}
      {props.portalId &&
        createPortal(
          <div className="t-pointer-events-auto" id={finalPortalId} />,
          document.body
        )}
      <ReactDatePicker
        {...props}
        portalId={finalPortalId}
        renderCustomHeader={(p) => (
          <CustomHeader
            {...p}
            showMonthDropdown={showMonthDropdown}
            showYearDropdown={showYearDropdown}
            showYearPicker={props.showYearPicker}
            showMonthYearPicker={props.showMonthYearPicker}
            showQuarterYearPicker={props.showQuarterYearPicker}
            maxDate={props.maxDate}
            minDate={props.minDate}
          />
        )}
        renderDayContents={(day, date) => {
          return date ? (
            <ConditionalToolTip
              keepOpenOnActivation={true}
              /* @tw */
              propClass="t-w-64 !t-text-left react-datepicker-ignore-onclickoutside"
              condition={
                props.minDate &&
                dayjs(date).isBefore(props.minDate) &&
                getDisabledTooltipContent?.(date, props.minDate, props.maxDate)
              }
            >
              <span className="t-flex t-h-full t-w-full t-justify-center t-items-center">
                {day}
              </span>
            </ConditionalToolTip>
          ) : (
            day
          );
        }}
        dayClassName={(day) => {
          const today = dayjs().format(YYYY_MM_DD);
          const dayDate = dayjs(day).format(YYYY_MM_DD);
          /* @tw */
          return classNames(
            "all:unset t-h-6 t-w-6 t-cursor-pointer t-flex t-justify-center  t-text-text-100 t-items-center hover:t-bg-purple-0 t-text-body-sm t-select-none t-rounded aria-disabled:!t-text-text-30 aria-disabled:!t-bg-surface-grey  aria-selected:!t-text-text aria-selected:!t-bg-purple aria-selected:hover:!t-bg-purple",
            {
              /* @tw */
              "!t-relative after:!t-content-[''] after:!t-absolute after:!t-w-4 after:!t-h-[2px] after:!t-bg-purple after:!t-left-[calc(50%_-_8px)] after:!t-bottom-0 after:!t-rounded-[2px]":
                today === dayDate,
            }
          );
        }}
        weekDayClassName={() => {
          /* @tw */
          return classNames(
            "all:unset t-w-6 t-flex t-justify-center t-items-center t-text-body-sm t-select-none"
          );
        }}
        /* @tw */
        calendarClassName={classNames(
          "all:unset !t-bg-white t-flex t-flex-col t-gap-4 [&>span]:t-hidden",
          {
            "t-min-h-[324px]":
              !props.showYearPicker ||
              !props.showMonthYearPicker ||
              !props.showQuarterYearPicker,
          }
        )}
        /* @tw */
        popperClassName={classNames(
          "all:unset reactDatePicker t-shadow-secondary t-bg-white t-z-datePicker t-rounded data-[placement=bottom-start]:!t-top-2 data-[placement=top-start]:!t-bottom-2 sm:data-[placement=top-start]:!t-bottom-auto !t-pb-0",
          {
            "t-w-[280px]": !props.showTimeSelectOnly,
          }
        )}
        /* @tw */
        wrapperClassName={classNames(
          "all:unset t-relative",
          { "t-w-full": props.block },
          props.wrapperClassName
        )}
        monthClassName={(date) => {
          const excludedMonthDisabled =
            excludeMonths?.includes(date.getMonth()) &&
            excludeYear?.includes(date.getFullYear());

          const {
            disableMaxMonthYear,
            disableMinMonthYear,
            minDateMonth,
            maxDateMonth,
          } = checkForMonthDisable({
            date,
            minDate: props.minDate,
            maxDate: props.maxDate,
          });

          const maxMonthDisabled =
            date.getMonth() > maxDateMonth! && disableMaxMonthYear;

          const minMonthDisabled =
            date.getMonth() < minDateMonth! && disableMinMonthYear;

          /* @tw */
          return classNames(
            "all:unset t-h-6 t-cursor-pointer !t-flex t-justify-center t-items-center hover:t-bg-purple-0 t-text-body-sm t-select-none t-rounded  t-text-text-100 aria-disabled:!t-text-text-30 aria-disabled:!t-bg-surface-grey aria-disabled:t-pointer-events-none aria-selected:!t-text-text aria-selected:!t-bg-purple-50 aria-selected:hover:!t-bg-purple-50 !t-m-0",
            {
              "!t-text-text-30 !t-bg-surface-grey t-pointer-events-none":
                excludedMonthDisabled || maxMonthDisabled || minMonthDisabled,
            }
          );
        }}
        showPopperArrow={false}
        onChange={onChange}
        name={props.name}
        // @ts-ignore
        onChangeRaw={onChangeRaw}
        dateFormat={dateFormat}
        placeholderText={placeholder}
        showMonthDropdown={showMonthDropdown}
        showYearDropdown={showYearDropdown}
        dropdownMode="select"
        // portalId="root-portal-for-calendar"
        required={required}
        customInput={
          customDatePickerInputTrigger ? (
            typeof customDatePickerInputTrigger === "string" ? (
              <div>{customDatePickerInputTrigger}</div>
            ) : (
              (customDatePickerInputTrigger as React.ReactElement)
            )
          ) : (
            <CustomInput
              label={label}
              required={required}
              placeholder={placeholder}
              {...props}
            />
          )
        }
        {...(selectedDate?.isValid()
          ? { selected: selectedDate.toDate() }
          : {})}
        autoComplete="off"
        preventOpenOnFocus={true}
        value={undefined}
      />
    </>
  );
};
